import { Card, ScreenerRuleSelect } from '@/components/UI';

interface ScreenerGatingCardProps {
    canAttachScreener: boolean;
    canEdit: boolean;
    isScreenerAttachmentLoading: boolean;
    onScreenerRuleIdChange: (screenerRuleId: number | null) => void;
    screenerRuleId: number | null;
    strategyId: number | null;
}

/**
 * Advisory text for the optimizer-parity warning the backend returns when a screener is attached.
 * Exported so the editor's save flow can surface it after it commits the attachment.
 */
export const buildOptimizerConfirmationNotice = (
    advisory?: { status: string; shouldWarn: boolean; message: string | null } | null
): string | null => {
    if (!advisory?.shouldWarn) return null;

    switch (advisory.status) {
        case 'confirmation_not_run':
            return 'Optimizer confirmation has not been run; use confirmation backtests only when you want optimizer/backtest parity evidence.';
        case 'confirmation_pending':
            return 'Optimizer confirmation is still queued or running.';
        case 'confirmation_not_passed':
            return 'The latest optimizer confirmation did not pass; review it before relying on optimizer parity.';
        case 'confirmation_unavailable':
            return 'Optimizer confirmation status is unavailable.';
        default:
            return advisory.message ?? 'Optimizer confirmation needs review.';
    }
};

/**
 * Screener selection for the strategy. Purely controlled — edits are staged into the editor's form
 * state and committed by the page's single save action, alongside every other change.
 */
const ScreenerGatingCard = ({
    canAttachScreener,
    canEdit,
    isScreenerAttachmentLoading,
    onScreenerRuleIdChange,
    screenerRuleId,
    strategyId
}: ScreenerGatingCardProps) => {
    return (
        <Card
            subtitle="An attached published screener is always applied. Detach it to run the strategy without a screener."
            title="Entry screener"
        >
            {!strategyId || !canAttachScreener ? (
                <div className="text-sm text-zinc-400">Save the strategy first to attach a screener.</div>
            ) : (
                <ScreenerRuleSelect
                    isDisabled={isScreenerAttachmentLoading || !canEdit}
                    label="Attached screener"
                    value={screenerRuleId}
                    onChange={onScreenerRuleIdChange}
                />
            )}
        </Card>
    );
};

export default ScreenerGatingCard;
